import {
  Box,
  Button,
  Center,
  chakra,
  Flex,
  Heading,
  ScaleFade,
  Stack,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
  Wrap,
} from '@chakra-ui/react'
import { faClock, faInfoCircle, faPencil, faShieldCheck } from '@fortawesome/pro-regular-svg-icons'
import { faBoltLightning } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime, Duration } from 'luxon'
import { useCallback, useEffect, useMemo, useState } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { noOp } from '@/utils/helpers'
import sleep from '@/utils/sleep'

import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import getLatestAgeAssuranceSession from '@/client/services/ageAssurance/getLatestAgeAssuranceSession'
import getNextAgeAssuranceStep from '@/client/services/ageAssurance/getNextAgeAssuranceStep'
import type {
  AgeAssuranceMethod,
  AgeAssuranceSessionConfig,
} from '@/client/services/ageAssurance/types'
import { trackEvent } from '@/client/services/analytics'
import { getArticleUrl } from '@/client/services/url/faq'

import AgeAssuranceMethodCard from '@/components/AgeAssurance/AgeAssuranceMethodCard'
import AgeAssuranceSessionModal, {
  type AgeAssuranceSessionModalProps,
  type AgeAssuranceSessionModalResult,
} from '@/components/AgeAssurance/AgeAssuranceSessionModal'
import UpdateAgeButton from '@/components/Profile/AgeDetail/UpdateAgeButton'
import PublicImage from '@/components/PublicImage/PublicImage'
import ShwLink from '@/components/ShwLink'
import Alert from '@/components/UI/Alert'

const YOTI_WAIT_TIME = Duration.fromObject({ milliseconds: 2500 })
const VERIFY_LATER_NOTICE_DELAY_MILLIS = 750

type PrepReminderProps = {
  illustrationImageName: string
  reminder: string
  infoTooltip?: string
}

const PREP_REMINDER_PROPS: PrepReminderProps[] = [
  {
    illustrationImageName: 'well-lit-space.svg',
    reminder: 'Find a private, well-lit space',
  },
  {
    illustrationImageName: 'device-camera-mic.svg',
    reminder: 'Use a device with a camera and microphone',
  },
  {
    illustrationImageName: 'face-coverings.svg',
    reminder: 'Remove any face coverings',
    infoTooltip: 'Religious face coverings are exempt.',
  },
]

export default function AgeAssuranceGuide({
  showExitButton = false,
  showVerifyLaterNotice = false,
  onExitButtonClick,
  onSessionModalDone,
  onAgeAssured,
}: {
  showExitButton?: boolean
  showVerifyLaterNotice?: boolean
  onExitButtonClick?: () => void
  onSessionModalDone?: AgeAssuranceSessionModalProps['onDone']
  onAgeAssured?: () => void
}) {
  const user = useUser()

  const age =
    user.dateOfBirth != null
      ? Math.floor(-DateTime.fromISO(user.dateOfBirth).diffNow('years').years)
      : null

  const [sessionHistory, setSessionHistory] = useState(user.ageAssuranceHistory)
  const [selectedMethod, setSelectedMethod] = useState<AgeAssuranceMethod>('yoti')

  const { latestSession, nextStep } = useMemo(
    () => ({
      latestSession: getLatestAgeAssuranceSession(sessionHistory),
      nextStep: getNextAgeAssuranceStep(sessionHistory),
    }),
    [sessionHistory]
  )

  useEffect(() => {
    trackEvent(
      {
        category: 'Age Assurance',
        action: 'Guide Update',
      },
      { latestSession, nextStep }
    )
  }, [latestSession, nextStep])

  const isAgeAssured = latestSession?.status === 'completed' && latestSession?.outcome === 'success'

  useEffect(() => {
    if (isAgeAssured) {
      onAgeAssured?.()
    }
  }, [isAgeAssured, onAgeAssured])

  const {
    isOpen: isSessionModalOpen,
    onClose: onSessionModalClose,
    onOpen: onSessionModalOpen,
  } = useDisclosure()

  const onSessionModalDoneInternal = useCallback(
    async (result: AgeAssuranceSessionModalResult) => {
      trackEvent(
        {
          category: 'Age Assurance',
          action: 'Finish Session',
        },
        { result }
      )
      onSessionModalClose()
      if (result === 'completed' && latestSession?.config.method === 'yoti') {
        // Give us a few seconds to receive and process Yoti's webhook
        setWaitingForYoti(true)
        await sleep(YOTI_WAIT_TIME.as('milliseconds'))
        setWaitingForYoti(false)
      }

      onSessionModalDone?.(result)
      if (result === 'later') {
        return
      }

      const newHistory = await RPC.getAgeAssuranceHistory({})
      setSessionHistory(newHistory)
    },
    [onSessionModalDone, onSessionModalClose, latestSession?.config.method]
  )

  const [isWaitingForYoti, setWaitingForYoti] = useState(false)
  const [sessionConfig, setSessionConfig] = useState<AgeAssuranceSessionConfig | null>(null)

  const [startSession, isStartingSession] = useAPIWrapper(
    async (preferredMethod?: AgeAssuranceMethod) => {
      const session = await RPC.findOrCreateAgeAssuranceSession({ preferredMethod })
      setSessionConfig(session.config)
      // Open the completion modal in a future tick to prevent a flashing effect
      // as the modal opens with session still set to null.
      setTimeout(() => {
        onSessionModalOpen()
      }, 0)
      trackEvent(
        {
          category: 'Age Assurance',
          action: 'Start Session',
        },
        { config: session.config }
      )
    },
    [onSessionModalOpen],
    'Start Age Assurance session'
  )

  let buttonText = 'Start'
  if (latestSession?.config.method === 'yoti' && latestSession?.status === 'pending') {
    buttonText = 'Resume'
  } else if (nextStep === 'yoti') {
    buttonText = 'Start'
  } else if (nextStep === 'choice') {
    buttonText = `Start ${selectedMethod === 'yoti' ? 'ID' : 'Manual'}`
  } else if (nextStep === 'videoask') {
    buttonText = 'Start Manual'
  }
  buttonText = `${buttonText} Verification`

  return (
    <Box width="100%">
      <AgeAssuranceSessionModal
        sessionConfig={sessionConfig ?? { method: 'videoask' }}
        onDone={onSessionModalDoneInternal}
        isOpen={isSessionModalOpen}
      />
      {isAgeAssured ? (
        <Stack align="center" gap="16px">
          <Center width="80px" height="80px" bgColor="green.subtle" borderRadius="full">
            <Text color="green.fg" fontSize="32px">
              <FontAwesomeIcon icon={faShieldCheck} />
            </Text>
          </Center>
          <Stack align="center" gap="4px" maxWidth="400px">
            <Heading as="h3" size="md" textAlign="center">
              Age Verification Complete
            </Heading>
            <Text color="fg.muted" textAlign="center">
              Your age has been successfully verified. You can now attend live sessions on
              Schoolhouse!
            </Text>
          </Stack>
          {showExitButton ? (
            <Button width="100%" onClick={onExitButtonClick != null ? onExitButtonClick : noOp}>
              Got It
            </Button>
          ) : null}
        </Stack>
      ) : latestSession?.config.method === 'videoask' && latestSession?.status === 'pending' ? (
        <Stack align="center" gap="16px">
          <Center width="80px" height="80px" bgColor="bg.subtle" borderRadius="full">
            <Text color="fg.muted" fontSize="32px">
              <FontAwesomeIcon icon={faClock} />
            </Text>
          </Center>
          <Stack align="center" gap="4px" maxWidth="400px">
            <Heading as="h3" size="md" textAlign="center">
              Manual Verification In Progress
            </Heading>
            <Text color="fg.muted" textAlign="center">
              If you&apos;ve submitted your VideoAsk, our Safety team will review it within 2
              business days.
            </Text>
          </Stack>
          <Text color="fg.muted" fontSize="sm" textAlign="center" mb="12px">
            Haven&apos;t submitted?{' '}
            <ShwLink onClick={() => startSession()} cursor="pointer">
              Start your submission
            </ShwLink>
            {' · '}
            Having trouble?{' '}
            <ShwLink href={getArticleUrl('how-do-i-contact-the-schoolhouse-team')}>
              Contact User Support
            </ShwLink>
          </Text>
          {showExitButton ? (
            <Button width="100%" onClick={onExitButtonClick != null ? onExitButtonClick : noOp}>
              Got It
            </Button>
          ) : null}
        </Stack>
      ) : (
        <Stack gap="32px" opacity={isWaitingForYoti ? 0.4 : 1} transition="opacity 0.15s">
          {latestSession?.config.method === 'yoti' && latestSession?.status === 'pending' ? (
            <Alert variant="info-blue">
              <Stack gap="4px">
                <Text fontWeight="bold">Pick up where you left off</Text>
                <Text fontWeight="normal" fontSize="14px">
                  Welcome back! It looks like you started to verify your age, but didn&apos;t
                  finish. When you&apos;re ready, click the button below to resume.
                </Text>
              </Stack>
            </Alert>
          ) : nextStep === 'yoti' ? (
            <Stack gap="16px">
              <Text>
                To protect all learners, we ask new users to verify their age before joining a
                session.{' '}
                <ShwLink href={getArticleUrl('age-verification-for-new-users')}>Learn more</ShwLink>
              </Text>
              {showVerifyLaterNotice ? (
                <ScaleFade in delay={VERIFY_LATER_NOTICE_DELAY_MILLIS / 1000}>
                  <Alert variant="info-blue" title="Short on time?">
                    <Text fontWeight={400}>
                      <chakra.span fontWeight={600}>Short on time? </chakra.span> Click{' '}
                      <chakra.span fontWeight={500}>Verify Later</chakra.span> to register now and
                      verify your age later.
                    </Text>
                  </Alert>
                </ScaleFade>
              ) : null}
            </Stack>
          ) : nextStep === 'choice' ? (
            <Alert variant="warning">
              <Stack gap="4px">
                <Text fontWeight="bold">We weren&apos;t able to verify you</Text>
                <Text fontWeight="normal" fontSize="14px">
                  But don&apos;t worry! You have more options. Choose from one of them below.
                </Text>
              </Stack>
            </Alert>
          ) : nextStep === 'videoask' ? (
            <Stack gap="12px">
              <Alert variant="warning">
                There was an issue verifying your age through our verification provider.
              </Alert>
              <Stack gap="4px">
                <Text>
                  To successfully verify your age, you&apos;ll need to complete our alternate manual
                  video verification process.
                </Text>
                <Text fontSize="sm" color="fg.muted">
                  Our Safety team will review your submission within 2 business days.
                </Text>
              </Stack>
            </Stack>
          ) : null}
          {nextStep === 'choice' ? (
            <Stack gap="16px">
              <AgeAssuranceMethodCard
                method="yoti"
                isSelected={selectedMethod === 'yoti'}
                onClick={() => setSelectedMethod('yoti')}
              />
              <AgeAssuranceMethodCard
                method="videoask"
                isSelected={selectedMethod === 'videoask'}
                onClick={() => setSelectedMethod('videoask')}
              />
            </Stack>
          ) : (
            <>
              <Wrap
                justify="center"
                spacingX={{ base: '0px', md: '50px' }}
                spacingY={{ base: '12px', md: '32px' }}
                justifyContent="center"
              >
                {PREP_REMINDER_PROPS.map(({ illustrationImageName, reminder, infoTooltip }) => (
                  <Flex
                    key={reminder}
                    alignItems="center"
                    direction={{ base: 'row', md: 'column' }}
                    width={{ base: '100%', md: '164px' }}
                    gap={{ base: '12px', md: '8px' }}
                  >
                    <Center
                      width={{ base: '56px', md: '164px' }}
                      height={{ base: '56px', md: '164px' }}
                      minWidth={{ base: '56px', md: '164px' }}
                      bgColor="bg.subtle"
                      borderRadius="8px"
                    >
                      <PublicImage
                        width={{ base: '32px', md: '100px' }}
                        src={`/age-assurance/${illustrationImageName}`}
                      />
                    </Center>
                    <Text
                      textAlign={{ base: 'left', md: 'center' }}
                      color="fg.muted"
                      fontSize="14px"
                      lineHeight="20px"
                    >
                      {reminder}
                      {infoTooltip != null ? (
                        <Tooltip label={infoTooltip}>
                          <Box ml="4px" display="inline-block" color="fg">
                            <FontAwesomeIcon icon={faInfoCircle} />
                          </Box>
                        </Tooltip>
                      ) : null}
                    </Text>
                  </Flex>
                ))}
              </Wrap>
              {latestSession?.config.method === 'yoti' &&
              latestSession?.status === 'pending' ? null : (
                <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  bgColor="bg.subtle"
                  borderRadius="12px"
                  border={`2px solid ${chakraTokenToVar('border')}`}
                  px="24px"
                  py="16px"
                >
                  <Flex alignItems="center" gap="12px">
                    <Heading color="fg.muted" size="lg">
                      My age is
                    </Heading>
                    <Heading size="2xl">{age ?? '—'}</Heading>
                  </Flex>
                  <UpdateAgeButton
                    hideVerificationNotice
                    variant="ghost"
                    leftIcon={<FontAwesomeIcon icon={faPencil} />}
                  >
                    Edit
                  </UpdateAgeButton>
                </Flex>
              )}
            </>
          )}
          <Button
            width="100%"
            isLoading={isStartingSession}
            onClick={() => startSession(nextStep === 'choice' ? selectedMethod : undefined)}
            rightIcon={
              <Tag
                size="sm"
                bgColor="primary.muted"
                color="primary.fg"
                fontWeight="bold"
                fontSize="11px"
                letterSpacing="0.05em"
                borderRadius="full"
              >
                <FontAwesomeIcon icon={faBoltLightning} style={{ marginRight: '4px' }} />2 MIN
              </Tag>
            }
          >
            {buttonText}
          </Button>
        </Stack>
      )}
    </Box>
  )
}
